import { useRef, useState } from "react";
import { Button, Input, Space, Table, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
// import type { InputRef } from 'antd';
import { cloneDeep } from "lodash";

import Icon from "@assets/icon_config";
import { useHospitalMappingStore } from "@stores/useHospitalMappingStore";
import "./table.scss";
import { FilterConfirmProps } from "antd/lib/table/interface";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { useConfigStore } from "@stores/useConfigStore";
import { CanEdit } from '@components/accessLevel'
import { TableLocale } from "@components/noDataFound";

const TableComponent = (props: any) => {

  const setIsAddTableRowModalOpen = useHospitalMappingStore((state) => state.setIsAddTableRowModalOpen);
  const currentpageMode = useHospitalMappingStore((state) => state.pageCurrentMode);
  const setModalData = useHospitalMappingStore((state) => state.setModalData);
  const setModalMode = useHospitalMappingStore((state) => state.setModalMode);
  const modalData = useHospitalMappingStore((state) => state.modalData);

  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  // const searchInput = useRef<InputRef>(null);

  const updateRow = (record: any) => {
    if (currentpageMode === "edit" && record.is_active === "Y") {
      console.log("row is updated");
      const cloneModalData = cloneDeep(modalData);
      cloneModalData.rowData = record;
      setModalMode("edit");
      setModalData(cloneModalData);
      setIsAddTableRowModalOpen(true);
    }
  };

  const rowSelection = {
    hideSelectAll: true,
    renderCell: (checked: any, record: any, index: any, originNode: any) => (
      <CanEdit hide={false}>
      <div
        onClick={(e) => {
          e.stopPropagation();
          props?.delete(record);
        }}
      >
        <span className="tableComponent__row-selection"><Icon name={record.is_active === "Y" ? "eye-non-restricted" : "eye-restricted"}/></span>
      </div>
      </CanEdit>
    ),
    onselect: (record: any, selected: any, selectedRows: any, nativeEvent: any) => console.log(record, selected, selectedRows, nativeEvent),
  };

  const highlighterDiv = (text: any, record: any) => {
    return (
      <Tooltip placement="topLeft" title={text ? text.toString() : ""}>
        <span className={`${record.is_active === "Y" ? "nonDeletedRow" : "deletedRow"} table-content-style`}>{text}</span>
      </Tooltip>
    );
  };

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: any,
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void, confirm: (param?: FilterConfirmProps) => void,) => {
    clearFilters();
    setSearchText('');
    confirm();
  };

  const inputref = useRef<any>(null);
  const getColumnSearchProps = (dataIndex: any, label: string): any => ({   
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }:any) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={inputref}
          placeholder={`Search ${label}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={(e) => {handleSearch(selectedKeys as string[], confirm, dataIndex); e.stopPropagation()}}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#EF6D2A' : undefined }} />
    ),
    onFilterDropdownOpenChange: (visible:any) => {
      if (visible) {
        setTimeout(() => inputref.current?.select(), 100);
      }
    },
    onFilter: (value : any, record : any) =>
    record[dataIndex] ? 
      record[dataIndex]?.toString()?.toLowerCase()?.includes((value as string)?.toLowerCase()) : "",
    render: (text: any, record: any) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0}}
          className={`${record.is_active === "Y" ? "nonDeletedRow" : "deletedRow"} table-content-style`}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        highlighterDiv( text, record )
      ),
  });


  const column: ColumnsType<any> = [
    ...props?.column
      ?.filter((col: any) => col?.is_display === "Y")
      ?.map((colConfig: any) => {
        return {
          title: colConfig?.table_header_name,
          dataIndex: colConfig?.column_key,
          key: colConfig?.column_key,
          className: "hospitalList-table-column",
          sorter: (a: any, b: any) => {
            a[colConfig?.column_key] = a[colConfig?.column_key] ? a[colConfig?.column_key] : "";
            b[colConfig?.column_key] = b[colConfig?.column_key] ? b[colConfig?.column_key] : "";
            return a[colConfig?.column_key]?.localeCompare(b[colConfig?.column_key]);
          },
          // render: (text: any, record: any) => highlighterDiv(text, record),
          ...getColumnSearchProps(colConfig?.column_key, colConfig?.column_display_name),
        };
      }),
  ];
  
  const canEdit = useConfigStore(
    store => store.canEdit
  )

  const dataSource = props?.data;
  return (
    <div className={`tableComponent ${currentpageMode === "edit" ? "tableEditMode" : ""}`}>
      <Table
        columns={props?.column || props?.column?.length > 0 ? column : []}
        dataSource={dataSource}
        rowSelection={ currentpageMode === "edit" ? rowSelection : undefined}
        onRow={(record, rowIndex) => {
          return {
            onClick: canEdit() ? (event) => {
              event.stopPropagation();
              updateRow(record);
            } : undefined,
          };
        }}
        bordered={true}
        pagination={{
          defaultPageSize: 5,
          showSizeChanger: true,
          pageSizeOptions: ["5", "10", "15", "20", "25", "30"],
        }}
        scroll={{ y: 300 }}
        locale={TableLocale}
      />
    </div>
  );
};

export default TableComponent;
